import { useMemo, useState } from "react";

import clsx from "clsx";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

import { Alert } from "../base/Alert";
import Card from "../shared/Card/Card";
import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import { useClipboardCopy } from "../../hooks/useClipboardCopy";
import { ICustomerSetting } from "../../models/CustomerSettings/CustomerSetting";
import useCustomerSettings from "../../data/CustomerSetting/useCustomerSettings";
import { CreateUpdateCustomerSettingModal } from "./CreateUpdateCustomerSettingModal";

interface CustomerSettingsListRowProps {
  index: number;
  setting: CustomerSettingWithSubKey;
  onEditClick: (setting: ICustomerSetting) => void;
}

export function CustomerSettingsListRow({
  index,
  setting,
  onEditClick,
}: CustomerSettingsListRowProps) {
  const { CopyToClipboardButton } = useClipboardCopy();
  const [show, setShow] = useState(false);

  return (
    <div className="py-2 px-4 grid grid-cols-12 gap-x-4" key={index}>
      <span className="col-span-2 text-sm font-medium">{setting.subKey}</span>
      {show ? (
        <span className="col-span-8 text-sm text-left font-mono text-gray-800 break-all">
          {setting.value ? (
            setting.value
          ) : (
            <span className="italic">{"<no-value>"}</span>
          )}
        </span>
      ) : (
        <span className="col-span-8" />
      )}
      <div className="col-span-2 flex items-center justify-end space-x-2">
        <Button size="xs" onClick={() => setShow((show) => !show)}>
          {show ? (
            <EyeSlashIcon className="w-4 h-4" />
          ) : (
            <EyeIcon className="w-4 h-4" />
          )}
        </Button>
        <CopyToClipboardButton copyText={setting.value} />
        <Button size="xs" onClick={() => onEditClick(setting)}>
          <PencilIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

interface CustomerSettingWithSubKey extends ICustomerSetting {
  subKey: string;
}

interface GroupedCustomerSettings {
  [key: string]: CustomerSettingWithSubKey[];
}

interface CustomerSettingsListGroupProps {
  groupKey: string;
  settings: CustomerSettingWithSubKey[];
  onEditClick: (setting: ICustomerSetting) => void;
}

function CustomerSettingsListGroup({
  groupKey,
  settings,
  onEditClick,
}: CustomerSettingsListGroupProps) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded((ex) => !ex);

  return (
    <div className="py-4" key={groupKey}>
      <div
        className={clsx(
          "flex items-center justify-between",
          expanded && "pb-4"
        )}
      >
        <span className="col-span-2 text-sm font-medium">{groupKey}</span>
        {expanded ? (
          <ChevronUpIcon
            className="w-6 h-6 text-gray-600"
            onClick={toggleExpanded}
          />
        ) : (
          <ChevronDownIcon
            className="w-6 h-6 text-gray-600"
            onClick={toggleExpanded}
          />
        )}
      </div>
      {expanded && (
        <div className="ml-6 border-t bg-gray-50 border-gray-100 divide-y divide-gray-100">
          {settings.map((setting, index) => (
            <CustomerSettingsListRow
              key={index}
              index={index}
              setting={setting}
              onEditClick={onEditClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CustomerSettingsListProps {
  customerId: string;
}

export function CustomerSettingsList({
  customerId,
}: CustomerSettingsListProps) {
  const { customerSettings } = useCustomerSettings(customerId);

  const [customerSettingOpen, setCustomerSettingOpen] = useState(false);
  const [updateCustomerSetting, setUpdateCustomerSetting] =
    useState<ICustomerSetting>();

  const handleEditCustomerSetting = (setting: ICustomerSetting) => {
    setUpdateCustomerSetting(setting);
    setCustomerSettingOpen(true);
  };

  const handleCloseCustomerSetting = () => {
    setUpdateCustomerSetting(undefined);
    setCustomerSettingOpen(false);
  };

  const groupedCustomerSettings = useMemo(() => {
    const groupedSettings: GroupedCustomerSettings = {};

    if (!customerSettings.data) return groupedSettings;

    customerSettings.data?.forEach((setting) => {
      const keyParts = setting.key.split(":");
      const key = keyParts[0];
      const subKey = keyParts[1];

      if (!groupedSettings[key]) {
        groupedSettings[key] = [];
      }

      groupedSettings[key].push({ ...setting, subKey });
    });

    return groupedSettings;
  }, [customerSettings.data]);

  return (
    <>
      <CreateUpdateCustomerSettingModal
        open={customerSettingOpen}
        setOpen={handleCloseCustomerSetting}
        customerId={customerId}
        setting={updateCustomerSetting}
      />
      <Card
        title="Settings"
        bodyClassName="px-4 divide-y divide-gray-100"
        titleRightContent={
          <Button
            onClick={() => setCustomerSettingOpen(true)}
            size="sm"
            className="my-[1px] flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5 -ml-1" />
            <p>Add Setting</p>
          </Button>
        }
      >
        {customerSettings.isLoading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : customerSettings.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error getting customer applications"
              message={errorResolver(customerSettings.error)}
            />
          </div>
        ) : customerSettings.data && customerSettings.data?.length > 0 ? (
          Object.keys(groupedCustomerSettings).map((key, i) => (
            <CustomerSettingsListGroup
              key={i}
              groupKey={key}
              settings={groupedCustomerSettings[key]}
              onEditClick={handleEditCustomerSetting}
            />
          ))
        ) : (
          <div className="py-10 flex justify-center">
            <span className="text-sm font-medium text-gray-400">
              No settings
            </span>
          </div>
        )}
      </Card>
    </>
  );
}
